// animatedLoops/LoopColorStrip.jsx
//
// The "Loop colors" strip (deck concept). Recolors the selected loop — the
// motion is untouched. Segment / Wheel auto-match pills feed the live segment /
// wheel colour in; 10 curated two-tone palette swatches (Violet default).
//
// G-56: every control here DRIVES a real value change via onChange — selecting a
// swatch or a pill re-applies the loop in the new colours.

import React from "react";
import PropTypes from "prop-types";
import { LOOP_PALETTES, paletteFromColor } from "./catalog";

// NEM-7: one selection accent across the panel — the brand accent #9B5AF8 (same
// as the sibling "Reuse Library" control). The loop FILLS keep #8F38EF (deck
// fills); only the SELECTION chrome (swatch/pill active state) reads as an accent.
const SELECT_ACCENT = "#9B5AF8";

const Swatch = ({ bg, fg, active, label, onClick }) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={active}
    title={label}
    onClick={onClick}
    style={{
      width: 26,
      height: 26,
      borderRadius: "50%",
      background: bg,
      border: active ? `2px solid ${SELECT_ACCENT}` : "1px solid #e1e1e1",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      padding: 0,
    }}
  >
    <span style={{ width: 12, height: 12, borderRadius: "50%", background: fg }} />
  </button>
);

const Pill = ({ label, color, active, onClick, title }) => (
  <button
    type="button"
    aria-pressed={active}
    title={title}
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 999,
      fontFamily: "DM Sans, sans-serif",
      fontSize: 12,
      fontWeight: 600,
      color: active ? "#fff" : "#123051",
      background: active ? SELECT_ACCENT : "#F3EEFF",
      border: "1px solid " + (active ? SELECT_ACCENT : "#e1e1e1"),
      cursor: "pointer",
    }}
  >
    <span style={{ width: 12, height: 12, borderRadius: "50%", background: color, border: "1px solid rgba(0,0,0,.1)" }} />
    {label}
  </button>
);

const LoopColorStrip = ({ value, onChange, segmentColor, wheelColor }) => {
  const currentId = typeof value === "string" ? value : value?.id;
  const currentName =
    (typeof value === "object" && value?.name) ||
    LOOP_PALETTES.find((p) => p.id === currentId)?.name ||
    "Violet";

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #eee" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span style={{ fontFamily: "DM Sans, sans-serif", fontSize: 13, fontWeight: 700, color: "#123051" }}>
          Loop colors
        </span>
        <span style={{ fontFamily: "DM Sans, sans-serif", fontSize: 12, color: "#565556" }}>
          {currentName}
        </span>
      </div>

      {(segmentColor || wheelColor) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {segmentColor && (
            <Pill
              label="Segment"
              title="Match Segment"
              color={segmentColor}
              active={currentId === "seg-match"}
              onClick={() =>
                onChange({ ...paletteFromColor(segmentColor), id: "seg-match", name: "Segment" })
              }
            />
          )}
          {wheelColor && (
            <Pill
              label="Wheel"
              title="Match Wheel"
              color={wheelColor}
              active={currentId === "wheel-match"}
              onClick={() =>
                onChange({ ...paletteFromColor(wheelColor), id: "wheel-match", name: "Wheel" })
              }
            />
          )}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {LOOP_PALETTES.map((p) => (
          <Swatch
            key={p.id}
            bg={p.bg}
            fg={p.fg}
            label={p.name}
            active={currentId === p.id}
            onClick={() => onChange(p.id)}
          />
        ))}
      </div>

      <div style={{ fontFamily: "DM Sans, sans-serif", fontSize: 11, color: "#565556", marginTop: 8 }}>
        Recolors the selected loop — the motion is untouched.
      </div>
    </div>
  );
};

LoopColorStrip.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  segmentColor: PropTypes.string,
  wheelColor: PropTypes.string,
};

export default LoopColorStrip;
